import { formatDistanceStrict } from 'date-fns';

import {
  formatNovaScheduleRecurrence,
  NO_ACCOUNT_SELECTED_SCHEDULE_LABEL,
  type NovaScheduleRecurrence,
  type NovaScheduleStatus,
} from '../../../../lib/nova-schedule-proposal';
import type { NovaScheduleRunRecord, NovaScheduleRecord } from '../../../../queries/nova-schedule-management';

export interface NovaScheduleAccountGroup {
  key: string;
  name: string;
  schedules: NovaScheduleRecord[];
}

export interface NovaScheduleRunGroup {
  key: string;
  label: string;
  runs: NovaScheduleRunRecord[];
}

export function groupNovaSchedulesByAccount(schedules: NovaScheduleRecord[]): NovaScheduleAccountGroup[] {
  const groups = new Map<string, NovaScheduleAccountGroup>();

  for (const schedule of schedules) {
    const key = schedule.accountUuid ?? 'personal';
    const group = groups.get(key) ?? {
      key,
      name: schedule.account?.name ?? NO_ACCOUNT_SELECTED_SCHEDULE_LABEL,
      schedules: [],
    };
    group.schedules.push(schedule);
    groups.set(key, group);
  }

  return [...groups.values()].sort((left, right) => left.name.localeCompare(right.name));
}

export function groupNovaScheduleRunsByMonth(runs: NovaScheduleRunRecord[], timezone: string): NovaScheduleRunGroup[] {
  const sortedRuns = [...runs].sort(
    (left, right) => new Date(right.occurrenceAt).getTime() - new Date(left.occurrenceAt).getTime(),
  );
  const groups = new Map<string, NovaScheduleRunGroup>();

  for (const run of sortedRuns) {
    const date = new Date(run.occurrenceAt);
    if (Number.isNaN(date.getTime())) continue;

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
    }).formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value ?? '';
    const month = parts.find((part) => part.type === 'month')?.value ?? '';
    const key = `${year}-${month}`;
    const group = groups.get(key) ?? {
      key,
      label: new Intl.DateTimeFormat('en-US', { timeZone: timezone, month: 'long' }).format(date),
      runs: [],
    };
    group.runs.push(run);
    groups.set(key, group);
  }

  return [...groups.values()];
}

export function formatNovaScheduleListTiming(schedule: NovaScheduleRecord, now = new Date()): string {
  const lastRun = schedule.lastRunAt
    ? `Ran ${formatDistanceStrict(new Date(schedule.lastRunAt), now, { addSuffix: true })}`
    : `Starts ${formatNovaScheduleDateTimeShort(schedule.nextRunAt ?? schedule.startAt, schedule.timezone)}`;
  return `${lastRun} · ${formatNovaScheduleRecurrence(toNovaScheduleRecurrenceInput(schedule.recurrence))}`;
}

export function formatNovaScheduleDateTimeShort(value: string | null | undefined, timezone: string): string {
  if (!value) return 'Not scheduled';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return value;
  }
}

export function formatNovaScheduleRunDate(value: string, timezone: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return value;
  }
}

export function formatNovaScheduleStatus(status: NovaScheduleStatus): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

export function formatNovaScheduleDelivery(schedule: Pick<NovaScheduleRecord, 'outputPolicy' | 'recurrence'>): string {
  if (schedule.recurrence.preset === 'ONCE') return 'Current chat';
  return schedule.outputPolicy === 'NEW_CHAT_PER_RUN' ? 'Fresh chat for every run' : 'Same schedule chat';
}

export function isSuccessfulNovaScheduleRun(status: NovaScheduleRunRecord['status']): boolean {
  return status === 'SUCCEEDED';
}

export function toNovaScheduleRecurrenceInput(
  recurrence: NovaScheduleRecord['recurrence'] | NovaScheduleRecurrence,
): NovaScheduleRecurrence {
  switch (recurrence.preset) {
    case 'ONCE':
      return { preset: recurrence.preset };
    case 'HOURLY':
      return { preset: recurrence.preset, minuteOfHour: recurrence.minuteOfHour ?? undefined };
    case 'DAILY':
    case 'WEEKDAYS':
      return { preset: recurrence.preset, timeOfDay: recurrence.timeOfDay ?? undefined };
    case 'WEEKLY':
      return {
        preset: recurrence.preset,
        timeOfDay: recurrence.timeOfDay ?? undefined,
        weekdays: recurrence.weekdays?.slice() ?? undefined,
        weekInterval: typeof recurrence.weekInterval === 'number' ? recurrence.weekInterval : undefined,
      };
    case 'MONTHLY':
      return {
        preset: recurrence.preset,
        timeOfDay: recurrence.timeOfDay ?? undefined,
        dayOfMonth: recurrence.dayOfMonth ?? undefined,
      };
  }
}
